















































import Vue from 'vue'
import { Component, Watch, Prop } from 'vue-property-decorator'
import { AssetsModule } from '@/store/modules/asset'
import { ErrorsModule } from '@/store/modules/error'
import { AssetFile, SingleAssetUsage, BoundingBox } from '@/store/modules/asset'
import { FrameClipper } from '@/components/products/framepuzzle/util/frameClipper'
import { ToolbarDeactivations, ToolbarUploads, ToolbarModule, Toolbar } from '@/store/modules/toolbar'
import { FramePuzzleModule, CollageInfo } from '@/store/modules/framePuzzle'
import { AppModule, Step } from "@/store/modules/app"
import { ImageRatio, ImagePosition } from '@/rest/models'

import EventBus from '@/utils/eventBus'

type ImageData = { url: string, style: string }

@Component({
    name: 'frame-puzzle-collage-editor'
})
export default class FramePuzzleCollageEditor extends Vue {

    @Prop() private backgroundImg!: string
    @Prop() private width!: number
    @Prop() private height!: number
    @Prop() private step!: number
    @Prop() private usageName!: string

    private dragging = false
    private dragX = 0
    private dragY = 0
    private imageClipper = FrameClipper.getCollageClipper(true, this.width, this.height, this.step, this.usageName)
    private blending = false
    private hasUploadListener = false
    private currentAssetId: number | undefined = 0
    private image = { url: '', style: '' }
    private oldImage = { url: '', style: '' }

    get hasClippingSlot() {
        return !!this.$slots['clipping']
    }

    get hasOverlaySlot() {
        return !!this.$slots['overlay']
    }

    get zoom() {
        return ToolbarModule.zoom
    }

    set zoom(zoom: number) {
        ToolbarModule.SetZoom(zoom)
    }

    get rotation() {
        return ToolbarModule.rotation
    }

    set rotation(rotation: number) {
        ToolbarModule.SetRotation(rotation)
    }

    get center() {
        return ToolbarModule.center
    }

    set center(center: [number, number]) {
        ToolbarModule.SetCenter(center)
    }

    get selectedAssets() {
        return AssetsModule.selectedAssets
    }

    get disabledMovement() {
        if (FramePuzzleModule.showImageUpload) {
            return true
        }
        return ToolbarModule.deactivated & ToolbarDeactivations.ZoomAndRotation
    }

    get error() {
        return ToolbarModule.error
    }

    get imageEditorWidth() {
        return FramePuzzleModule.imageEditorWidth
    }

    get imageEditorHeight() {
        return FramePuzzleModule.imageEditorHeight
    }

    get parts() {
        return FramePuzzleModule.parts
    }

    get puzzleAsset() {
        let usageName = this.imageClipper.usageName
        if (usageName) {
            return FramePuzzleModule.activeAssets[usageName]
        }
        return undefined
    }

    get scale() {
        return 1
    }

    get showPlus() {
        return !AppModule.showMenu && (FramePuzzleModule.showImageUpload || FramePuzzleModule.showPuzzle)
    }

    get showPointer() {
        if (FramePuzzleModule.showImageUpload) {
            return this.image.url === ''
        }
        return !AppModule.showMenu
    }

    get recoverPuzzleToogled() {
        return FramePuzzleModule.recoverPuzzleToggled
    }

    get active() {
        return this.usageName === "frameimage" && !this.disabledMovement
            && FramePuzzleModule.userFormat !== "" && this.image.url !== ""
    }

    isSelectable(asset: AssetFile): boolean {
        return asset.assetId !== undefined && asset.rect[0] > 0 && asset.rect[1] > 0 && asset.success
    }

    initiateUpload($event: MouseEvent | TouchEvent) {
        EventBus.$emit('IMAGE_UPLOAD', $event)
    }

    showImageUpload() {
        if (this.image.url === '') {
            FramePuzzleModule.SetShowOverlayUpload(true)
        }
    }

    @Watch('selectedAssets')
    selectedAssetsChanged(newSelectedAssets: AssetFile[]) {
        let asset: AssetFile | undefined = undefined
        if (this.usageName in FramePuzzleModule.activeAssets) {
            asset = FramePuzzleModule.activeAssets[this.usageName]
        }
        if (this.image.url !== '') {
            this.oldImage = { url: this.image.url, style: this.image.style }
        } else {
            //@ts-ignore
            this.oldImage = { url: this.backgroundImg, style: '' }
        }
        setTimeout(() => {
            let usageName = this.imageClipper.usageName
            if (ToolbarModule.errorKeys.includes(this.usageName) && ToolbarModule.errorKeys.length === 1) {
                ToolbarModule.SetToolbarError(false)
            }
            ToolbarModule.UnsetErrorKey(this.usageName)
            if (usageName) {
                ErrorsModule.SetError({ key: usageName })
            }
            if (usageName && asset !== undefined && asset.pendingUsage[usageName]) {
                let usage = asset.pendingUsage[usageName] as SingleAssetUsage
                if (newSelectedAssets[0].assetId !== this.currentAssetId) {
                    usage = {
                        rotation: 0,
                        zoom: 0,
                        center: [asset.rect[0] / 2.0, asset.rect[1] / 2.0]
                    }
                    this.currentAssetId = asset.assetId
                }
                this.imageClipper.initializeUsage(usage, asset.rect)
                this.image = { url: asset.url, style: this.imageClipper.transformation }
                ToolbarModule.Initialize({ rotation: usage.rotation, zoom: usage.zoom, center: [...usage.center] as [number, number] })
                let info: CollageInfo = new CollageInfo(asset.assetId as number, this.imageClipper.boundingBox as BoundingBox, usage.rotation as number, false)
                if (!FramePuzzleModule.allCollages[usageName] || info.attributesChanged(FramePuzzleModule.allCollages[usageName])) {
                    info.attributesNeedUpdate = true
                }
                FramePuzzleModule.SetCollageUsageAndInfo([usageName, info])
            } else {
                this.imageClipper.reset()
                this.image = { url: '', style: '' }
                ToolbarModule.Initialize({ rotation: 0, zoom: 0, center: [0, 0] })
            }
            setTimeout(() => {
                this.blending = true
                setTimeout(() => {
                    this.oldImage = { url: '', style: '' }
                    this.blending = false
                }, 500)
            }, 100)
        }, 100)
    }

    @Watch('imageEditorHeight')
    imageEditorHeightChanged(newImageEditorHeight: number) {
        AppModule.SetCustomPuzzleClipper([this.imageEditorWidth, newImageEditorHeight])
        this.$nextTick(() => {
            if (ToolbarModule.errorKeys.includes(this.usageName) && ToolbarModule.errorKeys.length === 1) {
                ToolbarModule.SetToolbarError(false)
            }
            ToolbarModule.UnsetErrorKey(this.usageName)
            if (this.usageName) {
                ErrorsModule.SetError({ key: this.usageName })
            }
            let newWidth = this.imageEditorWidth
            let newHeight = this.imageEditorHeight
            this.imageClipper = FrameClipper.getCollageClipper(true, newWidth, newHeight, this.step, this.usageName)
            let usageName = this.imageClipper.usageName
            let asset: AssetFile | undefined = this.puzzleAsset
            if (usageName && asset !== undefined) {
                let usage = asset.pendingUsage[usageName] as SingleAssetUsage
                if (usage === undefined) {
                    usage = asset.usage[usageName] as SingleAssetUsage
                }
                this.imageClipper.initializeUsage(usage, asset.rect)
                this.image = { ...this.image, style: this.imageClipper.transformation }
                ToolbarModule.Initialize({ rotation: usage.rotation, zoom: usage.zoom, center: [...usage.center] as [number, number] })
            }
            if (usageName && asset === undefined) {
                if (usageName in FramePuzzleModule.activeAssets) {
                    asset = FramePuzzleModule.activeAssets[usageName]
                    let usage = asset.pendingUsage[usageName] as SingleAssetUsage
                    this.imageClipper.initializeUsage(usage, asset.rect)
                    this.image = { ...this.image, style: this.imageClipper.transformation }
                    ToolbarModule.Initialize({ rotation: usage.rotation, zoom: usage.zoom, center: [...usage.center] as [number, number] })
                }
            }
        })
    }

    @Watch('imageEditorWidth')
    imageEditorWidthChanged(newImageEditorWidth: number) {
        AppModule.SetCustomPuzzleClipper([newImageEditorWidth, this.imageEditorHeight])
        this.$nextTick(() => {
            if (ToolbarModule.errorKeys.includes(this.usageName) && ToolbarModule.errorKeys.length === 1) {
                ToolbarModule.SetToolbarError(false)
            }
            ToolbarModule.UnsetErrorKey(this.usageName)
            if (this.usageName) {
                ErrorsModule.SetError({ key: this.usageName })
            }
            let newWidth = this.imageEditorWidth
            let newHeight = this.imageEditorHeight
            this.imageClipper = FrameClipper.getCollageClipper(true, newWidth, newHeight, this.step, this.usageName)
            let usageName = this.imageClipper.usageName
            let asset: AssetFile | undefined = this.puzzleAsset
            if (usageName && asset !== undefined) {
                let usage = asset.pendingUsage[usageName] as SingleAssetUsage
                if (usage === undefined) {
                    usage = asset.usage[usageName] as SingleAssetUsage
                }
                this.imageClipper.initializeUsage(usage, asset.rect)
                this.image = { ...this.image, style: this.imageClipper.transformation }
                ToolbarModule.Initialize({ rotation: usage.rotation, zoom: usage.zoom, center: [...usage.center] as [number, number] })
            }
            if (usageName && asset === undefined) {
                if (usageName in FramePuzzleModule.activeAssets) {
                    asset = FramePuzzleModule.activeAssets[usageName]
                    let usage = asset.pendingUsage[usageName] as SingleAssetUsage
                    this.imageClipper.initializeUsage(usage, asset.rect)
                    this.image = { ...this.image, style: this.imageClipper.transformation }
                    ToolbarModule.Initialize({ rotation: usage.rotation, zoom: usage.zoom, center: [...usage.center] as [number, number] })
                }
            }
        })
    }

    @Watch('recoverPuzzleToogled')
    recoverPuzzleToogledChanged(newRecoverPuzzleToogled: boolean) {
        let asset = FramePuzzleModule.persistedAssets[this.usageName]
        let usage = FramePuzzleModule.persistedUsages[this.usageName] as SingleAssetUsage
        let info = FramePuzzleModule.persistedInfos[this.usageName]
        this.$nextTick(() => {
            if (asset) {
                this.imageClipper.initializeUsage(usage, asset.rect)
                this.image = { url: asset.url, style: this.imageClipper.transformation }
                this.currentAssetId = asset.assetId
                AssetsModule.SetPendingUsage([asset, this.usageName])
                AssetsModule.SetUsage([asset, this.usageName, usage])
                FramePuzzleModule.SetActiveAsset([this.usageName, asset])
                FramePuzzleModule.SetCollageUsageAndInfo([this.usageName, info])
            } else {
                this.imageClipper.reset()
                this.image = { url: '', style: '' }
                this.$nextTick(() => {
                    if (FramePuzzleModule.activeAssets[this.usageName]) {
                        FramePuzzleModule.UnsetActiveAsset(this.usageName)
                    }
                    if (FramePuzzleModule.allCollages[this.usageName]) {
                        FramePuzzleModule.ClearAllCollages(this.usageName)
                    }
                })
            }
        })
    }

    @Watch('zoom')
    zoomChanged(newZoom: number) {
        if (this.usageName === 'frameimage') {
            if (this.imageClipper.zoom !== newZoom) {
                this.imageClipper.zoom = newZoom
                this.image = { ...this.image, style: this.imageClipper.transformation }
            }
        }
    }

    @Watch('rotation')
    rotationChanged(newRotation: number) {
        if (this.usageName === 'frameimage') {
            if (this.imageClipper.rotation !== newRotation) {
                if (ToolbarModule.errorKeys.includes(this.usageName) && ToolbarModule.errorKeys.length === 1) {
                    ToolbarModule.SetToolbarError(false)
                }
                ToolbarModule.UnsetErrorKey(this.usageName)
                if (this.usageName) {
                    ErrorsModule.SetError({ key: this.usageName })
                }
                this.imageClipper.rotation = newRotation
                this.image = { ...this.image, style: this.imageClipper.transformation }
            }
        }
    }

    get filesLoading() {
        let filesLoading = false
        AssetsModule.assets.forEach(file => {
            if (!this.isSelectable(file)) {
                filesLoading = true
            }
        })
        return filesLoading
    }

    startDrag(evt: MouseEvent | TouchEvent) {
        if (this.disabledMovement || this.usageName !== 'frameimage') {
            return
        }
        if (evt instanceof MouseEvent) {
            if (evt.buttons === 1) {
                this.dragging = true;
                this.dragX = evt.pageX
                this.dragY = evt.pageY
            }
        } else {
            if (evt.touches.length === 1) {
                this.dragging = true
                this.dragX = evt.touches[0].pageX
                this.dragY = evt.touches[0].pageY
                evt.preventDefault()
            }
        }
    }

    doDrag(evt: MouseEvent | TouchEvent) {
        if (this.disabledMovement || this.usageName !== 'frameimage') {
            return
        }
        if (this.dragging) {
            if (evt instanceof MouseEvent) {
                this.imageClipper.move([evt.pageX - this.dragX, evt.pageY - this.dragY])
                this.dragX = evt.pageX
                this.dragY = evt.pageY
                this.image = { ...this.image, style: this.imageClipper.transformation }
            } else {
                if (evt.touches.length === 1 && this.dragging) {
                    this.imageClipper.move([evt.touches[0].pageX - this.dragX, evt.touches[0].pageY - this.dragY])
                    this.dragX = evt.touches[0].pageX
                    this.dragY = evt.touches[0].pageY
                    this.image = { ...this.image, style: this.imageClipper.transformation }
                    evt.preventDefault()
                }
            }
        }
    }

    stopDrag() {
        if (this.disabledMovement || this.usageName !== 'frameimage') {
            return
        }
        this.dragging = false;
    }

    mounted() {
        window.addEventListener('mouseup', this.stopDrag)
        window.addEventListener("touchend", this.stopDrag)
        window.addEventListener('mousemove', this.doDrag)
        EventBus.isRegistered('IMAGE_UPLOAD', (isRegistered: boolean) => {
            this.hasUploadListener = isRegistered
        })
    }

    beforeDestroy() {
        if (ToolbarModule.errorKeys.includes(this.usageName) && ToolbarModule.errorKeys.length === 1) {
            ToolbarModule.SetToolbarError(false)
        }
        ToolbarModule.UnsetErrorKey(this.usageName)
        if (this.usageName) {
            ErrorsModule.SetError({ key: this.usageName })
        }
        window.removeEventListener('mouseup', this.stopDrag)
        window.removeEventListener('touchend', this.stopDrag)
        window.removeEventListener('mousemove', this.doDrag)
        EventBus.stopRegistered('IMAGE_UPLOAD')
    }

    sameSize(collageImagePossition1: ImagePosition, collageImagePossition2: ImagePosition) {
        let sameWidth = false
        let sameHeight = false
        if (collageImagePossition1 != undefined && collageImagePossition2 != undefined &&
            collageImagePossition1.x1 != undefined && collageImagePossition2.x1 != undefined &&
            collageImagePossition1.x2 != undefined && collageImagePossition2.x2 != undefined &&
            collageImagePossition1.y1 != undefined && collageImagePossition2.y1 != undefined &&
            collageImagePossition1.y2 != undefined && collageImagePossition2.y2 != undefined) {
            let width1 = collageImagePossition1.x2 - collageImagePossition1.x1
            let width2 = collageImagePossition2.x2 - collageImagePossition2.x1
            if (width1 === width2) {
                sameWidth = true
            } else if (width1 > width2) {
                if (width1 - width2 <= 1) {
                    sameWidth = true
                }
            } else {
                if (width2 - width1 <= 1) {
                    sameWidth = true
                }
            }
            let height1 = collageImagePossition1.y2 - collageImagePossition1.y1
            let height2 = collageImagePossition2.y2 - collageImagePossition2.y1
            if (height1 === height2) {
                sameHeight = true
            } else if (height1 > height2) {
                if (height1 - height2 <= 1) {
                    sameHeight = true
                }
            } else {
                if (height2 - height1 <= 1) {
                    sameHeight = true
                }
            }
        }
        return sameWidth && sameHeight
    }
}
